<script setup lang="ts">
  import { reactiveOmit } from '@vueuse/core';
  import type { DialogContentEmits, DialogContentProps } from 'reka-ui';
  import { DialogClose, DialogContent, DialogPortal, useForwardPropsEmits } from 'reka-ui';
  import type { HTMLAttributes } from 'vue';

  import { Button } from '@ui/components/button';
  import { useDismissHandler } from '@ui/utils/useDismissHandler';
  import type { DismissableProps } from '@ui/utils/useDismissHandler';
  import { cn } from '@ui/utils/utils';

  import SheetOverlay from './SheetOverlay.vue';

  interface SheetContentEmits extends DialogContentEmits {
    close: [];
  }

  interface SheetContentProps extends DialogContentProps, DismissableProps {
    class?: HTMLAttributes['class'];
    floating?: boolean;
    hideCloseBtn?: boolean;
    side?: 'top' | 'right' | 'bottom' | 'left';
  }

  const props = withDefaults(defineProps<SheetContentProps>(), {
    side: 'right',
  });

  const emits = defineEmits<SheetContentEmits>();

  defineOptions({
    inheritAttrs: false,
  });

  const delegatedProps = reactiveOmit(props, 'class', 'side');

  const forwarded = useForwardPropsEmits(delegatedProps, emits);

  const { handleDismissInteraction } = useDismissHandler({
    disableDismissOn: props.disableDismissOn,
  });
</script>

<template>
  <DialogPortal>
    <SheetOverlay />
    <DialogContent
      data-slot="sheet-content"
      :class="
        cn(
          'rounded-sm bg-background data-[state=open]:animate-in data-[state=closed]:animate-out fixed z-50 flex flex-col gap-4 shadow-lg transition ease-in-out data-[state=closed]:duration-300 data-[state=open]:duration-500',
          side === 'right' &&
            'data-[state=closed]:slide-out-to-right data-[state=open]:slide-in-from-right inset-y-2 right-2 h-auto w-3/4 border-l',
          side === 'left' &&
            'data-[state=closed]:slide-out-to-left data-[state=open]:slide-in-from-left inset-y-2 left-2 h-auto w-3/4 border-r',
          side === 'top' &&
            'data-[state=closed]:slide-out-to-top data-[state=open]:slide-in-from-top inset-x-2 top-2 h-auto border-b',
          side === 'bottom' &&
            'data-[state=closed]:slide-out-to-bottom data-[state=open]:slide-in-from-bottom inset-x-2 bottom-2 h-auto border-t',
          props.class,
        )
      "
      v-bind="{ ...$attrs, ...forwarded }"
      @escape-key-down="handleDismissInteraction('esc', $event) && emits('close')"
      @interact-outside="handleDismissInteraction('interact-outside', $event) && emits('close')"
      @close-auto-focus="() => emits('close')"
    >
      <slot />

      <DialogClose
        v-if="!hideCloseBtn"
        class="ring-offset-background focus:ring-ring data-[state=open]:bg-secondary absolute top-1.5 right-1.5 rounded-sm opacity-70 transition-opacity hover:opacity-100 focus:ring-2 focus:ring-offset-2 focus:outline-hidden disabled:pointer-events-none"
      >
        <Button variant="ghost" size="icon-sm" class="size-6.5">
          <i class="far fa-close" />
        </Button>
        <!--        <PhX class="size-4" /> -->
        <!--        <span class="sr-only">Close</span> -->
      </DialogClose>
    </DialogContent>
  </DialogPortal>
</template>
